import React from 'react';
import { FieldValues } from 'react-hook-form';
import ErrorInput from '@/components/ErrorInput/ErrorInput';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import Select from 'react-select';
import CustomValueContainer from '@/components/SelectFloatingLabel/CustomValueContainer';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import CustomMenuList from '@/components/SelectFloatingLabel/CustomMenuList';
import SingleValue from '@/components/SelectFloatingLabel/CustomSingleValue';
import CustomMultiValueContainer from '@/components/SelectFloatingLabel/CustomMultiValueContainer';
import CustomOption from '@/components/SelectFloatingLabel/CustomOption';
import { ValueContainer } from 'react-select/animated';
import { FieldPath } from 'react-hook-form/dist/types';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

export type SelectOptionData = { [id in IKeyTypes]: string };

type IKeyTypes = 'label' | 'value';

type SelectFloatingLabelProps<
  T extends FieldValues,
  TName extends FieldPath<T>,
  I extends SelectOptionData
> = {
  items: Array<I>;
  errors?: FieldErrors<T>;
  label: string;
  field: ControllerRenderProps<T, TName>;
  required?: boolean;
  disabled?: boolean;
  isMulti?: boolean;
};
const SelectFloatingLabel = <
  T extends FieldValues,
  TName extends FieldPath<T>,
  I extends Record<IKeyTypes, string> = Record<IKeyTypes, string>
>({
  items,
  field: { onChange, onBlur, value, name, ref },
  errors,
  disabled = false,
  isMulti = false,
  label,
  required = false
}: SelectFloatingLabelProps<T, TName, I>) => {
  return (
    <>
      <Select
        unstyled
        isDisabled={disabled}
        styles={{
          container: (provided) => ({
            ...provided,
            marginTop: 30
          }),
          valueContainer: (provided) => ({
            ...provided,
            overflow: 'visible'
          }),
          placeholder: (provided, state) => ({
            ...provided,
            position: 'absolute',
            top: state.hasValue || state.selectProps.inputValue ? -24 : -2,
            border: state.hasValue || state.selectProps.inputValue ? 'none !important' : '',
            color: 'rgb(107 114 128 / var(--tw-text-opacity))',
            transition: 'top 0.1s, font-size 0.1s',
            fontSize: (state.hasValue || state.selectProps.inputValue) && 12
          })
        }}
        id={name}
        options={items}
        className="mt-8"
        onChange={onChange}
        components={{
          ValueContainer: CustomValueContainer as typeof ValueContainer,
          DropdownIndicator: () => <ChevronDownIcon className="h-5 w-5 text-gray-400" />,
          Option: CustomOption,
          MultiValueContainer: CustomMultiValueContainer,
          MenuList: CustomMenuList,
          SingleValue: SingleValue
        }}
        placeholder={required ? label + ' *' : label}
        isMulti={isMulti}
        onBlur={onBlur}
        value={value}
        name={name}
        ref={ref}
      />
      {errors && errors[name] && <ErrorInput error={errors[name]?.message?.toString()} id={name} />}
    </>
  );
};

export default SelectFloatingLabel;
